import type {
  GetArticlesByCategoryPaginationQuery,
  Locale,
} from '@seek/cmsu-cms-connect';
import { useTranslations } from '@vocab/react';
import {
  Box,
  ContentBlock,
  Heading,
  Link,
  Stack,
  Tiles,
} from 'braid-design-system';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { useLoaderData } from 'react-router-dom';

import { PageLayout } from '../components/PageLayout/PageLayout';
import { useAuth, useConfig } from '../shared/hooks';
import { CMS_PAGE_DISPLAYED_EVENT } from '../shared/utils/tealiumTracker/constants';
import {
  getLoggedInStatus,
  trackEvent,
} from '../shared/utils/tealiumTracker/tealiumAdapterHelper';

import {
  SubscriptionForm,
  SearchBox,
  Breadcrumbs,
} from '@seek/cmsu-components';
import { useFetchSuggestions } from '../shared/hooks/useFetchSuggestions';
import translations from './.vocab';
import { WPT_SALESFORCE_API } from '../../config';
import { ArticleCard } from '@seek/cmsu-components/src/modules/LatestArticlesByCategory/ArticleCard';
import { convertMelwaysLocale } from '../../helpers/convertMelwaysLocale';
import { getLocales } from '../shared/utils/getLocales';
import { CMSUProvider } from '@seek/cmsu-components/src/hooks/useCMSUContext';
import { useMelwaysLink } from '@seek/melways-react';
import { getBreadcrumbItems } from './helper';

export const ArticlesCategory = () => {
  const { slug } = useParams();
  const { authenticationStatus } = useAuth();

  const {
    melwaysLocale,
    sourceName,
    privacyUrl,
    country,
    language,
    section,
    site,
    route,
  } = useConfig();
  const data = useLoaderData() as GetArticlesByCategoryPaginationQuery;
  const articles = data.articles;
  const category = articles?.[0].category;

  useEffect(() => {
    const { isPending, isLoggedIn } = getLoggedInStatus(authenticationStatus);
    if (!isPending) {
      trackEvent(CMS_PAGE_DISPLAYED_EVENT, {
        currentPage: category?.name.toLocaleLowerCase() || '',
        isLoggedIn,
      });
    }
  }, [category, slug, authenticationStatus]);

  const { t } = useTranslations(translations);
  const [searchInput, setSearchInput] = useState<string | undefined>();

  const locale = convertMelwaysLocale(melwaysLocale);
  const fetchedSuggestions = useFetchSuggestions(locale, searchInput, section);
  const urlResolver = useMelwaysLink();
  const parentCategoryName = category?.parent?.name || '';
  const parentCategorySlug = category?.parent?.slug || '';
  const items = getBreadcrumbItems(
    locale as Locale,
    section,
    parentCategoryName,
    parentCategorySlug,
    category?.name || '',
    category?.slug || '',
  );

  return (
    <PageLayout
      layoutProps={{
        title: category?.name || '',
        ...(category?.description?.text && {
          seo: { description: category.description.text },
        }),
        categoryName: slug,
        locales: getLocales(
          site,
          route.replace(`/${language}/`, ''),
          category?.sites || [],
          category?.localizations || [],
        ),
        section: { sectionName: section },
      }}
    >
      <CMSUProvider config={{ language }}>
        {category?.name && (
          <Box padding="large" background="surface">
            <ContentBlock width="medium">
              <Breadcrumbs items={items} />
            </ContentBlock>
          </Box>
        )}

        <Box paddingBottom="xlarge" background="surface">
          <ContentBlock width="medium">
            {category && (
              <Box
                paddingBottom="large"
                paddingX={{
                  mobile: 'large',
                  tablet: 'xlarge',
                  desktop: 'none',
                }}
              >
                <Heading level="1">{category.name}</Heading>
              </Box>
            )}
            <Box
              paddingTop="xxsmall"
              paddingX={{
                mobile: 'large',
                tablet: 'xlarge',
                desktop: 'none',
              }}
              paddingBottom="xsmall"
            >
              <SearchBox
                placeholderText={t('Search box suggestion')}
                searchButtonText={t('Search button text')}
                onSearch={(term) => {
                  window.location.href = urlResolver({
                    language,
                    path: `/article-search/${encodeURIComponent(
                      term,
                    )}/1?section=${section}`,
                  });
                }}
                minChars={3}
                minCharsErrorMessage={t('Minimum chars error message', {
                  minChars: '3',
                })}
                onKeyPress={setSearchInput}
                onSelect={(path) => {
                  window.location.href = path;
                }}
                titleSuggestions={fetchedSuggestions}
              />
            </Box>
            <Box
              paddingX={{
                mobile: 'large',
                tablet: 'xlarge',
                desktop: 'none',
              }}
            >
              <Stack space="large">
                <Tiles
                  space="gutter"
                  columns={{
                    mobile: 1,
                    tablet: 2,
                    desktop: 3,
                  }}
                >
                  {articles &&
                    articles.map((article) => (
                      <Link
                        href={urlResolver({
                          language,
                          path: `${
                            article.category?.relatedCategorySection
                              ?.sectionName
                              ? `/${article.category?.relatedCategorySection?.sectionName}`
                              : ''
                          }/article/${article.slug}`,
                        })}
                        key={article.id}
                      >
                        <ArticleCard article={article} />
                      </Link>
                    ))}
                </Tiles>
              </Stack>
            </Box>
            <Box
              background="brand"
              padding="gutter"
              marginTop="xlarge"
              marginX={{ mobile: 'large', desktop: 'none', tablet: 'none' }}
            >
              <SubscriptionForm
                type={section}
                locale={melwaysLocale}
                sourceName={sourceName}
                privacyUrl={privacyUrl}
                wptSalesForceApi={WPT_SALESFORCE_API}
                country={country}
                submitBtnStyle={{ tone: 'brandAccent' }}
              />
            </Box>
          </ContentBlock>
        </Box>
      </CMSUProvider>
    </PageLayout>
  );
};
